// Detects a newer deploy without a manual hard-refresh.
//
// The deploy serves fixed-named bundles (app.js) with no Cache-Control, so a
// browser can keep running stale JS for a while. Each build stamps itself with
// VUE_APP_BUILD_ID (see vue.config.js) and emits a version.json carrying the
// same id. The running tab polls that file (uncached) and, when the deployed id
// differs from the one it booted with, raises `updateState.available` so the UI
// can offer a one-tap refresh.
import { reactive } from "vue";

const BUILT_ID = process.env.VUE_APP_BUILD_ID || null;

export const updateState = reactive({
  available: false,
});

let started = false;

async function check() {
  if (updateState.available || !BUILT_ID) return;
  try {
    // Cache-bust + no-store so we read the deployed value, never a cached one.
    const resp = await fetch(`/version.json?_=${Date.now()}`, {
      cache: "no-store",
    });
    if (!resp.ok) return;
    const { buildId } = await resp.json();
    if (buildId && buildId !== BUILT_ID) updateState.available = true;
  } catch (e) {
    /* offline / transient — retry on the next tick */
  }
}

export function startUpdateChecks({ intervalMs = 120000 } = {}) {
  if (started || !BUILT_ID) return;
  started = true;
  setTimeout(check, 8000); // shortly after load
  setInterval(check, intervalMs); // then on a timer
  window.addEventListener("focus", check); // and whenever the tab refocuses
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
}

// A plain reload isn't enough: the bundles have fixed names, and a browser
// holding a still-fresh cached app.js (Firefox reuses it on reload) boots the
// old build again — and the banner comes straight back. Re-fetch every script
// and stylesheet with cache: 'reload' first, which overwrites the HTTP cache
// entries, then reload onto the new build.
export async function applyUpdate() {
  const urls = [
    ...Array.from(document.scripts, (s) => s.src),
    ...Array.from(document.querySelectorAll('link[rel="stylesheet"]'), (l) => l.href),
  ].filter((u) => u && new URL(u, location.href).origin === location.origin);
  try {
    await Promise.all(urls.map((u) => fetch(u, { cache: "reload" })));
  } catch (e) {
    /* best effort — reload regardless */
  }
  window.location.reload();
}
